<template>
  <div class="min-h-screen bg-gradient-to-b from-[#142a4a] via-[#18365f] to-[#1b4475]">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
      <div class="mb-6 flex justify-between items-start gap-4">
        <div class="min-w-0">
          <h1 class="text-3xl font-bold text-slate-100">{{ title }}</h1>
          <p v-if="subtitle" class="text-slate-400 mt-1 text-sm">
            {{ subtitle }}
          </p>
        </div>
        <button
          @click="openCreateModal"
          class="inline-flex shrink-0 items-center justify-center px-4 py-2 rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 text-sm font-semibold transition"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Novo</span>
        </button>
      </div>

      <slot name="filters" :load-items="applyFilters"></slot>

      <div v-if="loading" class="flex justify-center items-center h-64">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
      </div>

      <div v-else-if="error" class="bg-red-500/10 border border-red-400/40 text-red-300 px-4 py-3 rounded-lg">
        {{ error }}
      </div>

      <div v-else class="rounded-xl border border-cyan-400/30 bg-slate-900/80 backdrop-blur-sm shadow-xl shadow-black/30 overflow-hidden">
        <!-- Mobile: cards (bonito e legível) -->
        <div class="sm:hidden">
          <div v-if="validItems.length === 0" class="px-6 py-12 text-center text-gray-500">
            Nenhum registro encontrado
          </div>

          <div v-else class="divide-y divide-slate-700/70">
            <div
              v-for="item in validItems"
              :key="item.id"
              :class="['p-4 transition', item.blocked ? 'bg-red-950/30' : 'bg-slate-900/30']"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-slate-100 truncate">
                    {{ formatColumnValue(columns[0], item) }}
                  </p>
                  <p v-if="item.blocked" class="text-xs font-medium text-red-300 mt-1">
                    Bloqueado
                  </p>
                </div>

                <div class="flex items-center gap-2 flex-shrink-0">
                  <template v-if="textActionButtons">
                    <button
                      @click="editItem(item)"
                      class="px-3 py-1.5 text-xs rounded-lg border border-cyan-400/45 bg-cyan-400/5 text-cyan-200 hover:bg-cyan-400/12 hover:border-cyan-400/70 transition"
                    >
                      Editar
                    </button>
                    <button
                      v-if="itemAllowsDelete(item)"
                      @click="confirmDelete(item)"
                      class="px-3 py-1.5 text-xs rounded-lg border border-red-400/45 bg-red-500/5 text-red-200 hover:bg-red-500/20 hover:border-red-400/70 transition"
                    >
                      Excluir
                    </button>
                  </template>
                  <template v-else>
                    <button
                      @click="editItem(item)"
                      class="text-cyan-400 hover:text-cyan-300 p-1"
                      title="Editar"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      v-if="itemAllowsDelete(item)"
                      @click="confirmDelete(item)"
                      class="text-red-400 hover:text-red-300 p-1"
                      title="Excluir"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </template>
                </div>
              </div>

              <div class="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <template v-for="column in columns" :key="column.key">
                  <div class="text-slate-400 truncate">{{ column.label }}</div>
                  <div class="text-slate-100 font-medium truncate">
                    {{ formatColumnValue(column, item) }}
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop: tabela -->
        <div class="hidden sm:block overflow-x-auto">
          <table class="min-w-full divide-y divide-slate-700/70">
            <thead class="bg-slate-800/80">
              <tr>
                <th
                  v-for="column in columns"
                  :key="column.key"
                  class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider"
                >
                  {{ column.label }}
                </th>
                <th
                  class="py-3 text-xs font-medium text-slate-300 uppercase tracking-wider whitespace-nowrap"
                  :class="textActionButtons ? 'px-3 text-left w-[150px]' : 'px-6 text-right'"
                >
                  Ações
                </th>
              </tr>
            </thead>
            <tbody class="bg-slate-900/30 divide-y divide-slate-700/70">
              <template v-if="validItems.length === 0">
                <tr>
                  <td :colspan="columns.length + 1" class="px-6 py-12 text-center text-slate-400">
                    Nenhum registro encontrado
                  </td>
                </tr>
              </template>
              <template v-else>
                <tr
                  v-for="item in validItems"
                  :key="item.id"
                  :class="['transition', item.blocked ? 'bg-red-950/30 hover:bg-red-950/40' : 'hover:bg-cyan-400/10']"
                >
                  <td
                    v-for="column in columns"
                    :key="column.key"
                    class="px-6 py-4 whitespace-nowrap text-sm text-slate-100"
                  >
                    <template v-if="column.format">
                      {{ column.format(item[column.key], item) }}
                    </template>
                    <template v-else>
                      {{ item[column.key] || '-' }}
                    </template>
                  </td>
                  <td
                    class="py-4 whitespace-nowrap text-sm font-medium"
                    :class="textActionButtons ? 'px-3 text-left w-[150px]' : 'px-6 text-right'"
                  >
                    <div v-if="textActionButtons" class="inline-flex items-center justify-start gap-2">
                      <button
                        @click="editItem(item)"
                        class="px-3 py-1.5 text-xs rounded-lg border border-cyan-400/45 bg-cyan-400/5 text-cyan-200 hover:bg-cyan-400/12 hover:border-cyan-400/70 transition"
                      >
                        Editar
                      </button>
                      <button
                        v-if="itemAllowsDelete(item)"
                        @click="confirmDelete(item)"
                        class="px-3 py-1.5 text-xs rounded-lg border border-red-400/45 bg-red-500/5 text-red-200 hover:bg-red-500/20 hover:border-red-400/70 transition"
                      >
                        Excluir
                      </button>
                    </div>
                    <template v-else>
                      <button
                        @click="editItem(item)"
                        class="text-cyan-400 hover:text-cyan-300 mr-4"
                      >
                        <svg class="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        v-if="itemAllowsDelete(item)"
                        @click="confirmDelete(item)"
                        class="text-red-400 hover:text-red-300"
                      >
                        <svg class="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </template>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        
        <div v-if="pagination.count > 0" class="px-6 py-4 border-t border-slate-700/70 bg-slate-900/40">
          <div v-if="pagination.pages <= 1" class="flex items-center justify-center">
            <p class="text-sm text-slate-300">
              Total de
              <span class="font-medium">{{ pagination.count }}</span>
              {{ pagination.count === 1 ? 'registro' : 'registros' }}
            </p>
          </div>
          
          <div v-else class="flex items-center justify-between">
            <div class="flex-1 flex justify-between sm:hidden">
              <button
                @click="changePage(pagination.page - 1)"
                :disabled="!pagination.prev"
                class="relative inline-flex items-center px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-300 bg-slate-900/70 hover:bg-slate-800/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                @click="changePage(pagination.page + 1)"
                :disabled="!pagination.next"
                class="ml-3 relative inline-flex items-center px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-300 bg-slate-900/70 hover:bg-slate-800/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próxima
              </button>
            </div>
            <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div class="flex items-center space-x-4">
              <p class="text-sm text-slate-300">
                Mostrando
                <span class="font-medium">{{ pagination.from }}</span>
                a
                <span class="font-medium">{{ pagination.to }}</span>
                de
                <span class="font-medium">{{ pagination.count }}</span>
                resultados
              </p>
            </div>
            <div>
              <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  @click="changePage(pagination.page - 1)"
                  :disabled="!pagination.prev"
                  class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-slate-600 bg-slate-900/70 text-sm font-medium text-slate-400 hover:bg-slate-800/80 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                </button>
                
                <template v-for="page in visiblePages" :key="page">
                  <button
                    v-if="page !== '...'"
                    @click="changePage(page)"
                    :class="[
                      'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                      page === pagination.page
                        ? 'z-10 bg-cyan-400/15 border-cyan-400/50 text-cyan-300'
                        : 'bg-slate-900/70 border-slate-600 text-slate-400 hover:bg-slate-800/80'
                    ]"
                  >
                    {{ page }}
                  </button>
                  <span
                    v-else
                    class="relative inline-flex items-center px-4 py-2 border border-slate-600 bg-slate-900/70 text-sm font-medium text-slate-300"
                  >
                    ...
                  </span>
                </template>

                <button
                  @click="changePage(pagination.page + 1)"
                  :disabled="!pagination.next"
                  class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-slate-600 bg-slate-900/70 text-sm font-medium text-slate-400 hover:bg-slate-800/80 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="isModalOpen || loadingItemDetails"
      class="fixed inset-0 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <div v-if="loadingItemDetails" class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 p-8 flex flex-col items-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mb-4"></div>
        <p class="text-slate-300">Carregando dados...</p>
      </div>
      
      <div v-else class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div class="px-6 py-4 bg-[#2f4563] border-b border-cyan-200/25 flex justify-between items-center flex-shrink-0">
          <h2 class="text-xl font-semibold text-slate-100">
            {{ editingItem ? 'Editar' : 'Novo' }} {{ singularTitle }}
          </h2>
          <button @click="closeModal" class="text-slate-500 hover:text-cyan-300 transition">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form @submit.prevent="openSaveConfirm" class="flex flex-col flex-1 min-h-0">
          <div class="px-6 py-4 overflow-y-auto flex-1 [scrollbar-width:thin] [scrollbar-color:rgba(103,232,249,0.45)_rgba(47,69,99,0.4)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#2f4563]/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-cyan-300/45 [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-[#2f4563]/80 [&::-webkit-scrollbar-thumb:hover]:bg-cyan-300/70">
            <slot name="form" :form="form" :errors="formErrors"></slot>
          </div>

          <div class="px-6 py-4 border-t border-slate-600/70 flex flex-col gap-3 flex-shrink-0 bg-slate-700/55">
            <div
              v-if="modalFooterErrorMessages.length"
              role="alert"
              class="relative w-full rounded-lg border border-red-400/50 bg-red-500/15 pl-3 pr-10 py-2.5 text-sm text-red-100 leading-snug"
            >
              <button
                type="button"
                class="absolute top-2 right-2 p-1 rounded-md text-red-200/90 hover:text-red-50 hover:bg-red-500/25 transition leading-none"
                aria-label="Fechar aviso"
                @click="dismissModalValidationAlert"
              >
                <span class="text-lg font-light" aria-hidden="true">×</span>
              </button>
              <span class="font-semibold text-red-50 block mb-1 pr-1">Não foi possível salvar.</span>
              <span class="text-red-100/95 break-words pr-1">{{ modalFooterErrorMessages.join(' · ') }}</span>
            </div>
            <div class="flex justify-end gap-3">
              <button
                type="submit"
                :disabled="saving"
                class="px-4 py-2 bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold rounded-lg transition disabled:opacity-50"
              >
                {{ saving ? 'Salvando...' : 'Salvar' }}
              </button>
              <button
                type="button"
                @click="closeModal"
                class="px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800/70 transition"
              >
                Sair
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div
      v-if="deleteConfirmItem"
      class="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      @click.self="closeDeleteModal"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-2xl p-6 max-h-[90vh] overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(103,232,249,0.45)_rgba(47,69,99,0.4)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#2f4563]/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-cyan-300/45 [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-[#2f4563]/80 [&::-webkit-scrollbar-thumb:hover]:bg-cyan-300/70">
        <h3 class="flex items-center text-lg font-semibold text-slate-100 mb-4">
          <svg class="w-6 h-6 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z" clip-rule="evenodd" />
          </svg>
          Confirmação!
        </h3>
        
        <div v-if="!deleteError">
          <p class="text-slate-300 mb-6">
            Tem certeza que deseja excluir este registro?<br>Esta ação não pode ser desfeita.
          </p>
        </div>

        <div v-else class="mb-6">
          <div class="bg-red-500/10 border border-red-400/40 rounded-lg p-4 mb-4">
            <div class="flex items-start">
              <svg class="w-5 h-5 text-red-300 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
              </svg>
              <div class="flex-1">
                <h4 class="text-red-300 font-semibold mb-1">{{ deleteError }}</h4>
                <p class="text-red-200 text-sm mb-3">{{ deleteErrorMessage }}</p>
                <div class="bg-red-950/30 rounded-md p-3 border border-red-500/30">
                  <p class="text-sm text-slate-300">
                    <strong class="text-red-300">Dica:</strong> remova ou ajuste os registros vinculados a este item e tente novamente.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-end space-x-3">
          <button
            v-if="!deleteError"
            @click="deleteItem"
            :disabled="deleting"
            class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition disabled:opacity-50"
          >
            {{ deleting ? 'Excluindo...' : 'Sim' }}
          </button>          
          <button
            @click="closeDeleteModal"
            class="px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800/70 transition"
          >
            {{ deleteError ? 'Fechar' : 'Não' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="saveConfirmOpen"
      class="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      @click.self="closeSaveConfirm"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-md w-full p-6">
        <h3 class="flex items-center text-lg font-semibold text-slate-100 mb-4">
          <svg class="w-6 h-6 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z" clip-rule="evenodd" />
          </svg>
          Confirmação!
        </h3>
        <p class="text-slate-300 mb-6">
          Deseja salvar este {{ singularTitle.toLowerCase() }}?
        </p>
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            :disabled="saving"
            @click="confirmSave"
            class="px-4 py-2 bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold rounded-lg transition disabled:opacity-50"
          >
            {{ saving ? 'Salvando...' : 'Sim' }}
          </button>          
          <button
            type="button"
            @click="closeSaveConfirm"
            class="px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800/70 transition"
          >
            Não
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      required: false,
      default: null
    },
    singularTitle: {
      type: String,
      required: true
    },
    apiEndpoint: {
      type: String,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    formFields: {
      type: Object,
      required: true
    },
    customUpdateEndpoint: {
      type: Function,
      required: false,
      default: null
    },
    textActionButtons: {
      type: Boolean,
      required: false,
      default: false
    },
    /** Chave JSON no corpo (ex.: coordinator_group). Se omitido, deriva do último segmento do endpoint. */
    resourceName: {
      type: String,
      required: false,
      default: null
    },
    /** (item) => boolean — se false, o botão Excluir não é exibido para esse registro. */
    canDeleteItem: {
      type: Function,
      required: false,
      default: null
    }
  },

  data() {
    return {
      items: [],
      loading: false,
      error: null,
      isModalOpen: false,
      editingItem: null,
      form: {},
      formErrors: {},
      saving: false,
      deleteConfirmItem: null,
      deleting: false,
      deleteError: null,
      deleteErrorMessage: null,
      saveConfirmOpen: false,
      filterParams: {},
      loadingItemDetails: false,
      /** true quando a última carga usou `data` + `pagy` (ex.: usuários). */
      listHasPaginationMetadata: false,
      pagination: {
        page: 1,
        items: 20,
        count: 0,
        pages: 0,
        from: 0,
        to: 0
      }
    }
  },

  computed: {
    validItems() {
      if (!Array.isArray(this.items)) {
        return []
      }
      return this.items.filter(item => item && item.id)
    },

    visiblePages() {
      const current = this.pagination.page
      const total = this.pagination.pages
      const delta = 2
      const range = []
      const rangeWithDots = []

      for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
        range.push(i)
      }

      if (current - delta > 2) {
        rangeWithDots.push(1, '...')
      } else {
        rangeWithDots.push(1)
      }

      rangeWithDots.push(...range)

      if (current + delta < total - 1) {
        rangeWithDots.push('...', total)
      } else if (total > 1) {
        rangeWithDots.push(total)
      }

      return rangeWithDots
    },

    modalFooterErrorMessages() {
      const fe = this.formErrors || {}
      const out = []
      const seen = new Set()
      const push = (s) => {
        const t = String(s || '').trim()
        if (!t || seen.has(t)) return
        seen.add(t)
        out.push(t)
      }
      if (fe.general) push(fe.general)
      for (const k of Object.keys(fe)) {
        if (k === 'general') continue
        push(fe[k])
      }
      return out
    }
  },

  mounted() {
    this.loadItems()
  },

  methods: {
    async loadItems(params = {}) {
      this.loading = true
      this.error = null
      this.filterParams = params

      try {
        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')
        
        const allParams = { ...params, page: this.pagination.page }
        const queryString = new URLSearchParams(allParams).toString()
        const url = queryString ? `${this.apiEndpoint}?${queryString}` : this.apiEndpoint
        
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${userToken}`, 'Tenant-Authorization': `Bearer ${tenantToken}` }
        })
        
        if (response.data.data && response.data.pagy) {
          this.items = Array.isArray(response.data.data) ? response.data.data : []
          this.pagination = response.data.pagy
          this.listHasPaginationMetadata = true
        } else {
          this.items = Array.isArray(response.data) ? response.data : []
          this.listHasPaginationMetadata = false
        }
      } catch (err) {
        this.error = err.response?.data?.error || 'Erro ao carregar os dados'
      } finally {
        this.loading = false
      }
    },

    changePage(page) {
      this.pagination.page = page
      this.loadItems(this.filterParams)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    applyFilters(params = {}) {
      this.pagination.page = 1
      this.loadItems(params)
    },

    openCreateModal() {
      this.editingItem = null
      this.form = { ...this.formFields }
      this.formErrors = {}
      this.isModalOpen = true
    },

    formatColumnValue(column, item) {
      if (!column || !item) return '-'
      const value = item[column.key]
      if (column.format) return column.format(value, item)
      if (value === null || value === undefined || value === '') return '-'
      return value
    },

    itemAllowsDelete(item) {
      if (typeof this.canDeleteItem !== 'function') return true
      return this.canDeleteItem(item)
    },

    async editItem(item) {
      this.loadingItemDetails = true
      this.formErrors = {}
      
      try {
        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')
        const baseEndpoint = this.apiEndpoint.split('?')[0]
        
        const response = await axios.get(`${baseEndpoint}/${item.id}`, {
          headers: { Authorization: `Bearer ${userToken}`, 'Tenant-Authorization': `Bearer ${tenantToken}` }
        })
        
        this.editingItem = response.data
        this.form = { ...this.formFields, ...response.data }
        this.isModalOpen = true
      } catch (err) {
        this.formErrors.general = err.response?.data?.error || 'Erro ao carregar os dados'
      } finally {
        this.loadingItemDetails = false
      }
    },

    closeModal(opts = {}) {
      const skipListReload = opts && opts.skipListReload === true
      this.isModalOpen = false
      this.editingItem = null
      this.form = { ...this.formFields }
      this.formErrors = {}
      if (!skipListReload) {
        this.loadItems(this.filterParams)
      }
    },

    dismissModalValidationAlert() {
      this.formErrors = {}
    },

    openSaveConfirm() {
      this.saveConfirmOpen = true
    },

    async saveItem() {
      this.saving = true
      this.formErrors = {}

      try {
        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')
        const headers = { Authorization: `Bearer ${userToken}`, 'Tenant-Authorization': `Bearer ${tenantToken}` }
        const resourceName = this.getResourceName()
        const baseEndpoint = this.apiEndpoint.split('?')[0]

        // Enviar apenas os campos definidos em formFields
        const cleanedForm = {}
        for (const key in this.formFields) {
          const value = this.form[key]
          if (value === undefined) continue

          const normalizedValue = await this.normalizeValueForPayload(key, value)
          if (normalizedValue !== undefined) {
            cleanedForm[key] = normalizedValue
          }
        }

        const useFormData = this.formNeedsMultipart(cleanedForm)
        const payload = useFormData ? this.buildFormData(resourceName, cleanedForm) : { [resourceName]: cleanedForm }
        const requestConfig = { headers }

        if (this.editingItem) {
          const updateEndpoint = this.customUpdateEndpoint 
            ? this.customUpdateEndpoint(this.editingItem.id)
            : `${baseEndpoint}/${this.editingItem.id}`
          
          if (useFormData) {
            payload.append('_method', 'PATCH')
            await axios.post(updateEndpoint, payload, requestConfig)
          } else {
            await axios.patch(updateEndpoint, payload, requestConfig)
          }
        } else {
          await axios.post(baseEndpoint, payload, requestConfig)
        }

        this.closeModal()
      } catch (err) {
        console.error('Erro ao salvar:', err)
        console.error('Response data:', err.response?.data)

        const errorData = err.response?.data
        this.formErrors = {}

        if (errorData?.errors) {
          if (Array.isArray(errorData.errors)) {
            this.formErrors.general = errorData.errors.join(' · ')
          } else if (typeof errorData.errors === 'string') {
            this.formErrors.general = errorData.errors
          } else if (typeof errorData.errors === 'object' && errorData.errors !== null) {
            for (const [k, v] of Object.entries(errorData.errors)) {
              this.formErrors[k] = Array.isArray(v) ? v.join(', ') : String(v)
            }
          }
        } else if (errorData?.error) {
          this.formErrors.general =
            typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error)
        } else if (errorData?.message) {
          this.formErrors.general = errorData.message
        } else if (err.message) {
          this.formErrors.general = err.message
        } else {
          this.formErrors.general = 'Erro ao salvar'
        }
      } finally {
        this.saving = false
      }
    },

    confirmSave() {
      this.saveConfirmOpen = false
      this.saveItem()
    },

    closeSaveConfirm() {
      this.saveConfirmOpen = false
    },

    /** URL devolvida pela API — não reenviar (Active Storage interpreta como signed id → 500). */
    isReplayedActiveStorageProfileImageUrl(value) {
      if (typeof value !== 'string' || value.length === 0) return false
      const s = value.trim()
      if (s.startsWith('data:') || s.startsWith('blob:')) return false
      return s.includes('/rails/active_storage/')
    },

    async normalizeValueForPayload(key, value) {
      if (key === 'attachments' && Array.isArray(value)) {
        const fileOnly = value.filter(item => this.isFileLike(item))
        return fileOnly.length > 0 ? fileOnly : undefined
      }

      if (key === 'profile_image' && typeof value === 'string') {
        if (this.isReplayedActiveStorageProfileImageUrl(value)) {
          return undefined
        }
        if (value.startsWith('data:image')) {
          try {
            const res = await fetch(value)
            return await res.blob()
          } catch {
            return value
          }
        }
      }

      return value
    },

    formNeedsMultipart(cleanedForm) {
      return Object.values(cleanedForm).some(value => this.valueHasFile(value))
    },

    valueHasFile(value) {
      if (this.isFileLike(value)) return true
      if (Array.isArray(value)) {
        return value.some(item => this.isFileLike(item))
      }
      return false
    },

    isFileLike(value) {
      return (typeof File !== 'undefined' && value instanceof File) ||
        (typeof Blob !== 'undefined' && value instanceof Blob)
    },

    buildFormData(resourceName, cleanedForm) {
      const formData = new FormData()

      for (const key in cleanedForm) {
        const value = cleanedForm[key]

        if (Array.isArray(value)) {
          if (value.length === 0) {
            formData.append(`${resourceName}[${key}][]`, '')
          } else {
            value.forEach(item => {
              if (item === undefined || item === null) return
              formData.append(`${resourceName}[${key}][]`, item)
            })
          }
        } else if (value !== undefined) {
          formData.append(`${resourceName}[${key}]`, value === null ? '' : value)
        }
      }

      return formData
    },

    confirmDelete(item) {
      this.deleteConfirmItem = item
      this.deleteError = null
      this.deleteErrorMessage = null
    },

    closeDeleteModal() {
      this.deleteConfirmItem = null
      this.deleteError = null
      this.deleteErrorMessage = null
    },

    /**
     * Atualiza a lista e a paginação sem GET completo quando a API já devolveu pagy.
     * Se a página atual ficar vazia mas ainda houver registos, recarrega só essa página.
     */
    async applyItemDeletedLocally(deletedId) {
      this.items = this.items.filter((item) => String(item?.id) !== String(deletedId))
      if (!this.listHasPaginationMetadata) return

      const p = this.pagination
      if (!p || typeof p.count !== 'number') return

      const itemsPerPage = p.items || 20
      const page = p.page || 1
      const newCount = Math.max(0, p.count - 1)
      const newPages = newCount === 0 ? 1 : Math.max(1, Math.ceil(newCount / itemsPerPage))

      if (this.items.length === 0) {
        if (newCount === 0) {
          this.pagination = {
            ...p,
            count: 0,
            pages: 1,
            last: 1,
            from: 0,
            to: 0,
            prev: null,
            next: null
          }
          return
        }
        this.pagination.page = Math.min(page, newPages)
        await this.loadItems(this.filterParams)
        return
      }

      const from = (page - 1) * itemsPerPage + 1
      const to = Math.min(page * itemsPerPage, newCount)
      this.pagination = {
        ...p,
        count: newCount,
        pages: newPages,
        last: newPages,
        from,
        to,
        prev: page > 1 ? page - 1 : null,
        next: page < newPages ? page + 1 : null
      }
    },

    async deleteItem() {
      this.deleting = true
      this.error = null

      try {
        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')
        const baseEndpoint = this.apiEndpoint.split('?')[0]
        const deletedId = this.deleteConfirmItem.id
        await axios.delete(`${baseEndpoint}/${deletedId}`, {
          headers: { Authorization: `Bearer ${userToken}`, 'Tenant-Authorization': `Bearer ${tenantToken}` }
        })

        this.closeDeleteModal()
        this.closeModal({ skipListReload: true })
        await this.applyItemDeletedLocally(deletedId)
      } catch (err) {
        if (err.response?.status === 409) {
          this.deleteError = err.response.data.error
          this.deleteErrorMessage = err.response.data.message
        } else {
          this.error = err.response?.data?.error || 'Erro ao excluir'
          this.closeDeleteModal()
        }
      } finally {
        this.deleting = false
      }
    },

    getResourceName() {
      if (this.resourceName) return this.resourceName
      const endpoint = this.apiEndpoint.split('?')[0]
      const lastSegment = endpoint.split('/').pop()
      return lastSegment.slice(0, -1)
    }
  }
}
</script>
